/**
 * ConfilexHourglass — Sablier animé Confilex (retro/pixel style).
 * Composant réutilisable pour toute opération asynchrone longue.
 *
 * Props :
 *   - stage (str)       : identifiant d'étape ("preparation" | "analysis" | "finalization" | "custom")
 *   - message (str)     : message principal affiché sous le sablier
 *   - substeps (Array)  : optionnel — liste de sous-étapes à afficher progressivement
 *   - error (str|null)  : optionnel — si non null, affiche un état d'erreur au lieu de l'attente
 *   - onRetry (fn)      : optionnel — callback pour le bouton "Réessayer"
 *   - size (str)        : "sm" | "md" | "lg" (default "md")
 *
 * Style : palette Confilex ivory/gold sur fond forestier, pixel-art rétro.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

const SIZE_MAP = {
  sm: { box: "w-16 h-16", pixel: 2 },
  md: { box: "w-24 h-24", pixel: 3 },
  lg: { box: "w-32 h-32", pixel: 4 },
};

/**
 * Sablier pixel-art SVG animé.
 * Le sable "coule" via animations CSS pures (pas d'assets externes).
 */
function HourglassSvg({ pixel = 3 }) {
  return (
    <svg
      viewBox="0 0 32 40"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
      shapeRendering="crispEdges"
      style={{ imageRendering: "pixelated" }}
    >
      {/* Cadre bois (or Confilex) */}
      <g fill="#C9A961">
        <rect x="4" y="0" width="24" height="2" />
        <rect x="4" y="38" width="24" height="2" />
        <rect x="6" y="2" width="20" height="1" />
        <rect x="6" y="37" width="20" height="1" />
      </g>
      {/* Verre du sablier */}
      <g fill="#0B3A2A" stroke="#F5EEDC" strokeWidth="0.5">
        <path d="M8 3 L24 3 L24 5 L20 12 L20 20 L24 27 L24 35 L8 35 L8 27 L12 20 L12 12 L8 5 Z" />
      </g>
      {/* Sable en haut — se vide */}
      <g className="cb-hg-top">
        <rect x="10" y="5" width="12" height="2" fill="#F5EEDC" />
        <rect x="11" y="7" width="10" height="1" fill="#F5EEDC" />
        <rect x="12" y="8" width="8" height="1" fill="#F5EEDC" />
        <rect x="13" y="9" width="6" height="1" fill="#F5EEDC" />
        <rect x="14" y="10" width="4" height="1" fill="#F5EEDC" />
      </g>
      {/* Grain de sable qui tombe (animé) */}
      <g className="cb-hg-fall">
        <rect x="15" y="14" width="2" height="2" fill="#F5EEDC" />
      </g>
      {/* Sable en bas — se remplit */}
      <g className="cb-hg-bottom">
        <rect x="10" y="33" width="12" height="1" fill="#F5EEDC" />
        <rect x="11" y="32" width="10" height="1" fill="#F5EEDC" />
        <rect x="12" y="31" width="8" height="1" fill="#F5EEDC" />
        <rect x="13" y="30" width="6" height="1" fill="#F5EEDC" />
        <rect x="14" y="29" width="4" height="1" fill="#F5EEDC" />
      </g>
      <style>{`
        @keyframes cb-hg-drop {
          0% { transform: translateY(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(16px); opacity: 0; }
        }
        @keyframes cb-hg-empty {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(1px); opacity: 0.7; }
        }
        @keyframes cb-hg-fill {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        .cb-hg-fall { animation: cb-hg-drop 1.6s ease-in-out infinite; transform-origin: center; }
        .cb-hg-top  { animation: cb-hg-empty 6s ease-in-out infinite; }
        .cb-hg-bottom { animation: cb-hg-fill 6s ease-in-out infinite; transform-origin: bottom center; }
      `}</style>
    </svg>
  );
}

export function ConfilexHourglass({
  stage = "custom",
  message = "Confilex prépare votre demande…",
  substeps = null,
  error = null,
  onRetry = null,
  size = "md",
  testid = "confilex-hourglass",
}) {
  const [visibleSubsteps, setVisibleSubsteps] = useState(0);
  const cfg = SIZE_MAP[size] || SIZE_MAP.md;

  // Fait apparaître progressivement les sous-étapes (une par une, toutes les 2s)
  useEffect(() => {
    if (!substeps || !substeps.length) return;
    let i = 1;
    setVisibleSubsteps(1);
    const iv = setInterval(() => {
      i += 1;
      if (i > substeps.length) {
        clearInterval(iv);
        return;
      }
      setVisibleSubsteps(i);
    }, 2200);
    return () => clearInterval(iv);
  }, [substeps]);

  if (error) {
    return (
      <div
        className="flex flex-col items-center justify-center py-10 px-6 text-center"
        data-testid={`${testid}-error`}
        role="alert"
      >
        <div className={`${cfg.box} mb-4 flex items-center justify-center rounded-lg bg-red-900/20 border border-red-500/40`}>
          <AlertTriangle className="w-10 h-10 text-red-400" />
        </div>
        <p className="cb-text-ivory font-semibold text-base mb-2">Une erreur est survenue</p>
        <p className="text-sm text-[#BFC8C2] max-w-md mb-4">{error}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="cb-btn-gold inline-flex items-center gap-2 text-sm"
            data-testid={`${testid}-retry`}
          >
            <RefreshCw className="w-4 h-4" /> Réessayer sans re-payer
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center justify-center py-10 px-6 text-center"
      data-testid={testid}
      data-stage={stage}
      role="status"
      aria-live="polite"
    >
      <div className={`${cfg.box} mb-5`}>
        <HourglassSvg pixel={cfg.pixel} />
      </div>
      <p className="cb-serif cb-text-ivory text-lg sm:text-xl font-semibold mb-3">
        {message}
      </p>
      {substeps && substeps.length > 0 && (
        <ul className="space-y-1.5 mt-2 max-w-md" data-testid={`${testid}-substeps`}>
          {substeps.slice(0, visibleSubsteps).map((s, i) => (
            <li
              key={i}
              className="text-xs text-[#BFC8C2] flex items-start gap-2 justify-center"
              style={{
                animation: "cb-fadein 0.5s ease-in",
                opacity: i === visibleSubsteps - 1 ? 1 : 0.6,
              }}
            >
              <span className="cb-gold shrink-0">▸</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      )}
      <style>{`
        @keyframes cb-fadein {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 0.6; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default ConfilexHourglass;
